import { createSlice } from '@reduxjs/toolkit';
import { loadMapConfig } from 'client/store/mapConfigSlice/mapConfigSlice';
import { defaultStyle, generateStyles } from './assetStyles';
import { stratify } from 'd3-hierarchy';
import * as styleRuleEvaluator from 'client/utils/evaluators/styleRuleEvaluator';

/**
 * This file includes functions that read data from the map configuration,
 * structure it for display (i.e. create a hierarchy of layer groups and
 * layers), and makes this available within the state of the application for
 * use elsewhere.
 */

const defaultId = 'root';
export const defaultHierarchy = stratify()([{ id: defaultId }]);

export const sectors = {
    DRAINAGE: 'drainage',
    TELCO: 'telco',
    ELECTRICITY: 'electricity',
    GAS: 'gas',
    THERMAL: 'thermal',
    WATER: 'water',
    SEWER: 'sewer',
    FUEL_AND_CHEMICALS: 'fuelandchemicals',
    TRANSPORT: 'transport',
    NON_UTILITY: 'nonutility',
    REFERENCE_AND_PLANNING: 'reference-and-planning'
};

export const defaultSectorSpecificStyles = {
    enabled: false,
    sector: null
};

const isNotNull = attr => {
    return attr !== null && typeof attr !== 'undefined';
};

const addZIndex = (node, zIndex) => {
    zIndex++;
    if (node?.children) {
        node?.children.sort((a, b) => b?.data?.displayOrder - a?.data?.displayOrder).forEach(child => {
            zIndex = addZIndex(child, zIndex);
        });
    } else {
        node.data.zIndex = zIndex;
    }
    return zIndex;
};

const getAssetTypesForVectorLayers = layers => {
    return layers
        .filter(layer => layer.assetType !== null && layer.sourceType?.toLowerCase() === 'vector')
        .map(layer => layer.assetType);
};

const noop = () => { };

const generateHierarchy = (layerGroups, layers, openLayerStyles, sectorSpecificStylesEnabled) => {
    const newLayerGroups = layerGroups.map(group => {
        return {
            id: group.id,
            parentId: isNotNull(group.parentId) ? group.parentId : defaultId,
            name: group.displayName,
            displayOrder: isNotNull(group.displayOrder) ? group.displayOrder : 0,
            onChange: group.onChange || noop,
            checked: isNotNull(group.isCheckedByDefault) ? group.isCheckedByDefault : true
        };
    });

    const newLayers = layers.map(layer => {
        const styleFunction = (feature, rules, sectorSpecificStylesEnabled) => {
            const styleRuleToApply = styleRuleEvaluator.evaluateStyleRules(feature, rules, sectorSpecificStylesEnabled);
            const style = openLayerStyles[styleRuleToApply?.styleId];
            if (typeof style === 'function') {
                return style(feature);
            }
            return styleRuleEvaluator.evaluateRotation(styleRuleToApply, feature, style);
        };

        return {
            id: layer.id,
            parentId: isNotNull(layer.layerGroup) ? layer.layerGroup : defaultId,
            name: layer.displayName,
            displayOrder: isNotNull(layer.displayOrder) ? layer.displayOrder : 0,
            onChange: layer.onChange || noop,
            checked: isNotNull(layer.isCheckedByDefault) ? layer.isCheckedByDefault : true,
            disabled: isNotNull(layer.disabled) ? layer.disabled : false,
            assetType: layer.assetType,
            sourceType: layer.sourceType,
            sourceProperties: layer.sourceProperties,
            style: (feature, sectorSpecificStylesEnabled) => styleFunction(feature, layer.styleRules, sectorSpecificStylesEnabled),
            minScale: layer.minimumScale || 1,
            maxScale: layer.maximumScale || Infinity
        };
    });

    const newItems = [...newLayerGroups, ...newLayers].sort((a, b) => a.displayOrder - b.displayOrder);
    const hierarchy = stratify()([{ id: defaultId }, ...newItems]).sort((a, b) => a.data?.displayOrder - b.data?.displayOrder);

    addZIndex(hierarchy, newItems.length);

    return hierarchy;
};

export const layersSlice = createSlice({
    name: 'layers',
    initialState: {
        result: {
            hierarchy: defaultHierarchy,
            typeNames: [],
            styles: defaultStyle
        },
        toggledLayers: [],
        sectorSpecificStyles: defaultSectorSpecificStyles,
        loading: false,
        error: false
    },
    reducers: {
        addToggledLayer: (state, action) => {
            const toggledLayer = action?.payload;
            const existingToggledLayers = [...state.toggledLayers];
            if (toggledLayer && !existingToggledLayers.includes(toggledLayer)) {
                existingToggledLayers.push(toggledLayer);
            }
            return { ...state, toggledLayers: existingToggledLayers };
        },

        removeToggledLayer: (state, action) => {
            const toggledLayer = action?.payload;
            const newToggledLayers = [...state.toggledLayers].filter(layer => layer !== toggledLayer);
            return { ...state, toggledLayers: newToggledLayers };
        },
        setSectorSpecificStyles: (state, action) => {
            const sectorSpecificStyles = {
                ...defaultSectorSpecificStyles,
                ...(action?.payload || {})
            };

            return {
                ...state,
                result: {
                    ...state.result
                },
                sectorSpecificStyles
            };
        }
    },
    extraReducers: {
        [loadMapConfig.pending]: state => {
            return {
                ...state,
                loading: true,
                error: false
            };
        },
        [loadMapConfig.fulfilled]: (state, action) => {
            const { layerGroups, layers, styles } = action?.payload || {};

            return {
                ...state,
                result: {
                    ...state.result,
                    hierarchy: layerGroups && layers ? generateHierarchy(layerGroups, layers, generateStyles(styles), state.sectorSpecificStyles) : defaultHierarchy,
                    typeNames: layers ? getAssetTypesForVectorLayers(layers) : [],
                    styles: styles ? generateStyles(styles) : { defaultStyle }
                },
                loading: false,
                error: false
            };
        },
        [loadMapConfig.rejected]: state => {
            return {
                ...state,
                result: {
                    ...state.result,
                    typeNames: [],
                    styles: { defaultStyle }
                },
                loading: false,
                error: true
            };
        }
    }
});

export const {
    addToggledLayer, removeToggledLayer, setSectorSpecificStyles
} = layersSlice.actions;

export default layersSlice.reducer;

export const selectSectorSpecificStyles = state => state.layers?.sectorSpecificStyles || defaultSectorSpecificStyles;
export const selectSectorSpecificStylesEnabled = state => !!selectSectorSpecificStyles(state)?.enabled;
